// @flow

import get from 'lodash/get';

import { selectCartUUID as selectCartId } from '@gandi/cart';
import type { FetchResponse } from '@gandi/resource/redux';

import { POST, PATCH } from 'gandi.redux-fetcher';

import type { Dispatch, GetState } from '../../types';

type Params = {
  id: ?number,
  process: string,
  product_type: string,
  payload: { ... },
};

function getIdFromConflictResponse(response: FetchResponse<>): ?string {
  return get(response, 'data.errors.[0].location', '').split('=')[1];
}

// PATCHing cart configuration using `application/merge-patch+json` content-type
// Allows for a deep merge of a resource's contents.
// (update fields inside `resource.payload` without replacing `resource.payload`
// Necessary for `autorenew` handling inside cart configurations.
// See:
//   - https://tools.ietf.org/html/rfc7396
//   - sequoia.api.cart!230
//   - T70223
export const upsertConfiguration =
  (
    params: Params,
    bypassConflict: boolean = true,
  ): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const { id, ...body } = params;

    const cartId = selectCartId(getState());

    const http = id ? PATCH : POST;
    const base = `/cart/carts/${cartId}/configurations`;
    const url = id ? `${base}/${id}` : base;

    return dispatch(
      http(url, body, {
        // $FlowFixMe[incompatible-exact]
        on20X: (response: FetchResponse<>) => response,
        // $FlowFixMe[incompatible-exact]
        not20X: (response: FetchResponse<>) => {
          // HTTP 409 Conflict on POST.
          // A configuration already exsists for the given process/product_type.
          // Perform the same call with `PATCH` method on said configuration.
          if (response.status === 409 && !id && bypassConflict) {
            const conflictId = getIdFromConflictResponse(response);

            if (conflictId) {
              return dispatch(
                upsertConfiguration(
                  {
                    ...params,
                    id: Number(conflictId),
                  },
                  false,
                ),
              );
            }
          }

          const err = new Error('Unable to upsert cart configuration.');
          // $FlowFixMe
          err.response = response;
          return Promise.reject(err);
        },
        headers: {
          'content-type':
            http === PATCH
              ? 'application/merge-patch+json'
              : 'application/json',
        },
      }),
    );
  };
